import React, { useState } from 'react';
import { Button } from './ui/button';
import { Calculator, TrendingDown, ArrowRight, CheckCircle, BarChart3, Zap, MessageCircle, AlertTriangle } from 'lucide-react';
import { images } from '../data/mock';
import { getTerminalSpotsLeft } from '../lib/urgency';

const CalculatorPage = () => {
  const [volume, setVolume] = useState('');
  const [rate, setRate] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [showZeroRateMessage, setShowZeroRateMessage] = useState(false);
  const [monthlyLoss, setMonthlyLoss] = useState(0);
  const [yearlyLoss, setYearlyLoss] = useState(0);
  const spotsLeft = getTerminalSpotsLeft();
  
  const ctaLink = "https://calendly.com/davwaysolution/review";

  const useEstimate = () => {
    setRate('3');
  };

  const calculate = () => {
    const vol = parseFloat(volume) || 50000;
    const r = parseFloat(rate);
    
    if (r === 0 || (rate !== '' && r === 0)) {
      setShowZeroRateMessage(true);
      setShowResult(false);
      return;
    }
    
    const actualRate = r || 3;
    const monthly = vol * (actualRate / 100);
    const yearly = monthly * 12;
    
    setMonthlyLoss(monthly);
    setYearlyLoss(yearly);
    setShowResult(true);
    setShowZeroRateMessage(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8" style={{ backgroundColor: '#0B1F3A' }}>
      <div className="w-full max-w-xl">
        {/* Business Logo - 5x header size */}
        <div className="flex justify-center mb-8">
          <img 
            src={images.logo} 
            alt="Davway Solutions" 
            className="w-auto"
            style={{ height: '280px' }}
            data-testid="calc-logo"
          />
        </div>

        {/* Calculator Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-4" style={{ backgroundColor: 'rgba(212, 175, 55, 0.2)', color: '#D4AF37' }}>
            <Calculator size={16} />
            <span>Profit Leak Calculator</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Are You Losing Money on Every Transaction?
          </h1>
          <p className="text-gray-300">
            Enter your numbers below to see how much profit is slipping away.
          </p>
        </div>

        {/* Urgency Banner */}
        <div className="flex items-center justify-center gap-2 rounded-xl px-4 py-3 mb-6" style={{ backgroundColor: 'rgba(255, 77, 45, 0.15)', border: '1px solid rgba(255, 77, 45, 0.4)' }}>
          <AlertTriangle size={18} style={{ color: '#FF4D2D' }} />
          <span className="font-bold text-sm" style={{ color: '#FF4D2D' }}>Only {spotsLeft} payment system spots left this month</span>
        </div>

        {/* Calculator Form */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 md:p-8">
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Monthly Card Volume
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                <input
                  type="number"
                  value={volume}
                  onChange={(e) => setVolume(e.target.value)}
                  placeholder="50,000"
                  className="w-full pl-8 pr-4 py-4 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500 transition-colors text-lg"
                  data-testid="calc-volume-input"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Current Processing Rate
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={rate}
                  onChange={(e) => setRate(e.target.value)}
                  placeholder="3"
                  className="w-full pl-4 pr-8 py-4 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500 transition-colors text-lg"
                  data-testid="calc-rate-input"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">%</span>
              </div>
              <p 
                onClick={useEstimate}
                className="text-sm mt-2 cursor-pointer hover:underline"
                style={{ color: '#F5C542' }}
              >
                Not sure? Use industry average (3%)
              </p>
            </div>
          </div>

          <Button 
            onClick={calculate}
            className="w-full text-white font-bold text-lg py-6 rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300"
            style={{ backgroundColor: '#22C55E' }}
            data-testid="calc-submit-btn"
          >
            <TrendingDown className="mr-2" size={20} />
            Find My Profit Leak
          </Button>
        </div>

        {/* Zero Rate Message */}
        {showZeroRateMessage && (
          <div className="mt-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-center mb-6">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: 'rgba(34, 197, 94, 0.2)' }}>
                <CheckCircle style={{ color: '#22C55E' }} size={28} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Great! You're Already at 0%</h3>
              <p className="text-gray-300 mb-4">But we offer additional benefits that can still help:</p>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="bg-white/5 rounded-xl p-3 text-center">
                <BarChart3 style={{ color: '#D4AF37' }} size={24} className="mx-auto mb-1" />
                <p className="text-white text-sm font-medium">Reporting</p>
              </div>
              <div className="bg-white/5 rounded-xl p-3 text-center">
                <Zap style={{ color: '#22C55E' }} size={24} className="mx-auto mb-1" />
                <p className="text-white text-sm font-medium">Fast Pay</p>
              </div>
              <div className="bg-white/5 rounded-xl p-3 text-center">
                <MessageCircle style={{ color: '#3B82F6' }} size={24} className="mx-auto mb-1" />
                <p className="text-white text-sm font-medium">24/7 Support</p>
              </div>
            </div>
            <div className="text-center">
              <a href={ctaLink} target="_blank" rel="noopener noreferrer">
                <Button 
                  className="text-white font-bold px-8 py-4 rounded-lg shadow-xl hover:scale-105 transition-all group"
                  style={{ backgroundColor: '#22C55E' }}
                >
                  Let's Talk
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                </Button>
              </a>
            </div>
          </div>
        )}

        {/* Results */}
        {showResult && (
          <div className="mt-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold mb-1" style={{ color: '#ef4444' }}>
                You may be losing ${monthlyLoss.toLocaleString()} / month
              </h3>
              <h3 className="text-3xl font-bold" style={{ color: '#ef4444' }}>
                ${yearlyLoss.toLocaleString()} / year
              </h3>
            </div>
            <p className="text-gray-300 text-center text-sm mb-2">
              This is a hidden profit leak most businesses never see.
            </p>
            <p className="text-center text-sm mb-4" style={{ color: '#22C55E' }}>
              Our payment systems can eliminate these fees completely.
            </p>
            {/* Urgency in results */}
            <div className="flex items-center justify-center gap-2 mb-4">
              <AlertTriangle size={16} style={{ color: '#FF4D2D' }} />
              <span className="font-bold text-sm" style={{ color: '#FF4D2D' }}>Only {spotsLeft} payment system spots left this month</span>
            </div>
            <div className="text-center">
              <a href={ctaLink} target="_blank" rel="noopener noreferrer">
                <Button 
                  className="text-white font-bold px-8 py-4 rounded-lg shadow-xl hover:scale-105 transition-all group"
                  style={{ backgroundColor: '#22C55E' }}
                  data-testid="calc-cta-btn"
                >
                  Fix My Profit Leak
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                </Button>
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CalculatorPage;
